import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { 
  auth, 
  db 
} from './firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import {
  Home, Users, User, Settings, LogOut, Plus, Search,
  ChevronRight, ArrowLeft, Activity, Calendar, Clock,
  AlertCircle, CheckCircle, XCircle, ExternalLink,
  BarChart3, FileText, Clipboard, Target,
  Brain, BookOpen, Scale, Shield, RefreshCw,
  Bell, Menu, X, Eye, AlertTriangle
} from 'lucide-react';
import './App.css';
// AI Integration
import AIAssistant from './AIAssistant';
import { PDFUploadForForm, PDFUploadPanel, listDocuments } from './PDFUpload';
import aiService from './aiService';
import { readAssessmentCode, INSTRUMENT_VERSION, compareAssessments } from './nmf';

// ============================================
// CONTEXT
// ============================================

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// ============================================
// CONSTANTS
// ============================================

const PHASES = {
  // A chart before any assessment has no phase, and must not claim one.
  // Order 0 so the first real reading always registers as movement.
  unassessed: { name: 'Not yet assessed', order: 0, color: '#64748b',
                description: 'No Analyzer assessment on record yet' },
  stabilization: {
    name: 'Stabilization',
    description: 'Building basic capacity and grounding',
    color: '#2d8a5d',
    order: 1
  },
  soft_articulation: {
    name: 'Soft Articulation',
    description: 'Validation, self-compassion, ratio building',
    color: '#6b5b95',
    order: 2
  },
  direction_recovery: {
    name: 'Direction Recovery',
    description: 'Full SW+A+T=D, rules navigation',
    color: '#4a6fa5',
    order: 3
  },
  process_resumption: {
    name: 'Process Resumption',
    description: 'Full toolkit, independent navigation',
    color: '#c97b63',
    order: 4
  }
};

const TOOLS = {
  swatd: {
    name: 'SWATD',
    description: 'Articulation, Validation Bank, Best Friend',
    url: 'https://swatd.com',
    icon: Target,
    phases: ['all']
  },
  clarity: {
    name: 'Clarity',
    description: 'Navigate alone',
    url: 'https://clarity.navigationalmind.com/',
    icon: Brain,
    phases: ['all']
  },
  rules: {
    name: 'Rules Framework',
    description: 'Situational navigation',
    url: 'https://therulesframework.com',
    icon: Scale,
    phases: ['all']
  },
  nw_inner_house: {
    name: 'The Inner House',
    description: 'Seven residents, and who is leading right now',
    url: 'https://www.thenavigationalway.com/the-inner-house.html',
    icon: Home,
    phases: ['all']
  },
  nw_alignment: {
    name: 'Functional Alignment',
    description: 'Four of seven, and why the house can always move',
    url: 'https://www.thenavigationalway.com/how-it-decides.html',
    icon: Users,
    phases: ['all']
  },
  nw_loop: {
    name: 'The Navigation Loop',
    description: 'Six stages \u2014 the circle and the spiral',
    url: 'https://www.thenavigationalway.com/circle-and-spiral.html',
    icon: RefreshCw,
    phases: ['all']
  },
  nw_collapse: {
    name: 'Collapse & Rebuild',
    description: 'What is happening when everything stops',
    url: 'https://www.thenavigationalway.com/when-it-collapses.html',
    icon: AlertTriangle,
    phases: ['all']
  },
  nw_anchors: {
    name: 'Anchors',
    description: 'What you grab when the ground shakes',
    url: 'https://www.thenavigationalway.com/anchors.html',
    icon: Shield,
    phases: ['all']
  },
  nw_articulation: {
    name: 'Articulation',
    description: 'Turning fog into something you can steer by',
    url: 'https://www.thenavigationalway.com/articulation.html',
    icon: Eye,
    phases: ['all']
  },
  nw_process_method: {
    name: 'The Process Method',
    description: 'Next True Step, Containment Window, Review Moment',
    url: 'https://www.thenavigationalway.com/process-method.html',
    icon: Clipboard,
    phases: ['all']
  },
  nw_way: {
    name: 'The Navigational Way',
    description: 'The daily companion \u2014 diary kept across devices',
    url: 'https://way.navigationalmind.com',
    icon: Calendar,
    phases: ['all']
  },
  nw_brake: {
    name: 'The Brake',
    description: "Things you can't undo",
    url: 'https://navigationalmind.com/the-brake.html',
    icon: AlertCircle,
    phases: ['all']
  },
  analyzer: {
    name: 'NM Analyzer',
    description: 'Clinical state assessment',
    url: 'https://analyzer.navigationalmind.com',
    icon: BarChart3,
    phases: ['all'],
    clinicianOnly: true
  },
  capacity: {
    name: 'Capacity Navigator',
    description: 'Decision-making capacity assessment',
    url: 'https://capacitynavigator.navigationalmind.com',
    icon: Shield,
    phases: ['all'],
    clinicianOnly: true
  }
};

// ============================================
// AUTH PROVIDER
// ============================================

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Fetch user profile from Firestore
        const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (profileDoc.exists()) {
          setUserProfile(profileDoc.data());
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profileDoc = await getDoc(doc(db, 'users', result.user.uid));
    if (profileDoc.exists()) {
      setUserProfile(profileDoc.data());
    }
    return result;
  };

  const registerClinician = async (email, password, name, credentials) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const profile = {
      uid: result.user.uid,
      email,
      name,
      credentials,
      role: 'clinician',
      status: 'pending',
      createdAt: serverTimestamp()
    };
    await setDoc(doc(db, 'users', result.user.uid), profile);
    setUserProfile(profile);
    return result;
  };

  const createPatient = async (patientData) => {
    // Create patient account
    const tempPassword = Math.random().toString(36).slice(-8) + 'A1!';
    const result = await createUserWithEmailAndPassword(auth, patientData.email, tempPassword);
    
    const profile = {
      uid: result.user.uid,
      email: patientData.email,
      displayName: patientData.displayName,
      role: 'patient',
      clinicianId: userProfile.uid,
      clinicianName: userProfile.name,
      phase: 'unassessed',
      phaseStartedAt: serverTimestamp(),
      status: 'active',
      consentGiven: false,
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'users', result.user.uid), profile);
    
    // Sign back in as clinician
    await signInWithEmailAndPassword(auth, user.email, ''); // This will fail, need different approach
    
    return { ...profile, tempPassword };
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
  };

  const value = {
    user,
    userProfile,
    loading,
    login,
    logout,
    registerClinician,
    createPatient,
    isAuthenticated: !!user,
    isClinician: userProfile?.role === 'clinician',
    isPatient: userProfile?.role === 'patient'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// PROTECTED ROUTE
// ============================================

function ProtectedRoute({ children, requireClinician = false, requirePatient = false }) {
  const { isAuthenticated, loading, isClinician, isPatient, userProfile } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireClinician && isClinician && userProfile?.status === 'pending') {
    return <PendingApprovalScreen />;
  }

  if (requireClinician && !isClinician) {
    return <Navigate to="/patient" replace />;
  }

  if (requirePatient && !isPatient) {
    return <Navigate to="/clinician" replace />;
  }

  return children;
}

// ============================================
// LOADING SCREEN
// ============================================

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p>Loading...</p>
    </div>
  );
}


// ============================================
// PENDING APPROVAL SCREEN
// ============================================

function PendingApprovalScreen() {
  const { logout, userProfile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>NM Rebuild</h1>
          <p>Clinical Coordinator</p>
        </div>
        <div className="auth-form" style={{ textAlign: 'center' }}>
          <Clock size={48} style={{ color: '#fbbf24', marginBottom: '16px' }} />
          <h2>Awaiting Approval</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            Your registration is pending administrator approval.
          </p>
          <button onClick={handleLogout} className="auth-btn primary">
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
// ============================================
// LOGIN PAGE
// ============================================

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for patient param in URL or localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const returnToPatientURL = urlParams.get('patient');
  const returnToPatientStorage = localStorage.getItem('returnToPatient');
  const returnToPatient = returnToPatientURL || returnToPatientStorage;

  // Store URL param in localStorage if present
  useEffect(() => {
    if (returnToPatientURL) {
      localStorage.setItem('returnToPatient', returnToPatientURL);
    }
  }, [returnToPatientURL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Redirect based on role will happen in useEffect
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      if (userProfile.role === 'clinician') {
        // Preserve patient param if returning from a tool
        if (returnToPatient) {
          localStorage.removeItem('returnToPatient'); // Clear after use
          navigate(`/clinician?patient=${returnToPatient}`);
        } else {
          navigate('/clinician');
        }
      } else if (userProfile.role === 'patient') {
        localStorage.removeItem('returnToPatient'); // Clear for patients
        navigate('/patient');
      }
    }
  }, [userProfile, navigate, returnToPatient]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>NM Rebuild</h1>
          <p>Clinical Coordinator</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Sign In</h2>
          
          {error && <div className="auth-error">{error}</div>}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <button type="submit" className="auth-btn primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p><a href="/forgot-password">Forgot password?</a></p>
          <p>Clinician? <a href="/register">Register here</a></p>
          <p style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--gray-200)' }}>
            <a href="https://navigationalmind.com">&larr; Back to navigationalmind.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// REGISTER PAGE (Clinician)
// ============================================

function RegisterPage() {
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { registerClinician } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await registerClinician(email, password, name, credentials);
      navigate('/clinician');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>NM Rebuild</h1>
          <p>Clinical Coordinator</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Clinician Registration</h2>
          
          {error && <div className="auth-error">{error}</div>}
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dr. Jane Smith"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Credentials</label>
            <input
              type="text"
              value={credentials}
              onChange={(e) => setCredentials(e.target.value)}
              placeholder="MD, PhD, etc."
              required
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          
          <button type="submit" className="auth-btn primary" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <a href="/login">Sign in</a></p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CLINICIAN DASHBOARD
// ============================================

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>NM Rebuild</h1>
          <p>Clinical Coordinator</p>
        </div>

        {success ? (
          <div className="auth-form">
            <div className="success-message">
              <CheckCircle size={48} />
              <h4>Check Your Email</h4>
              <p>We've sent a password reset link to <strong>{email}</strong></p>
              <p>Click the link in the email to reset your password, then return here to sign in.</p>
            </div>
            <div className="auth-footer" style={{ background: 'none', padding: '0' }}>
              <p><a href="/login">← Back to Sign In</a></p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <h2>Reset Password</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            {error && <div className="auth-error">{error}</div>}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {!success && (
          <div className="auth-footer">
            <p><a href="/login">← Back to Sign In</a></p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// CLINICIAN DASHBOARD CONTINUED
// ============================================

function ClinicianDashboard() {
  const { userProfile, logout } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('patients');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadPatients();
  }, [userProfile]);

  // Auto-select patient from URL param (when returning from tools)
  useEffect(() => {
    if (patients.length > 0 && !selectedPatient) {
      const urlParams = new URLSearchParams(location.search);
      const patientId = urlParams.get('patient');
      if (patientId) {
        const patient = patients.find(p => p.id === patientId);
        if (patient) {
          setSelectedPatient(patient);
          // Clear the URL param
          window.history.replaceState({}, '', location.pathname);
        }
      }
    }
  }, [patients, location.search]);

  const loadPatients = async () => {
    if (!userProfile?.uid) return;
    
    try {
      const q = query(
        collection(db, 'users'),
        where('clinicianId', '==', userProfile.uid),
        where('role', '==', 'patient')
      );
      const snapshot = await getDocs(q);
      const patientList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPatients(patientList);

      // Keep the OPEN chart in step too. PatientDetail renders from
      // selectedPatient, which was captured when the chart was opened — so
      // refreshing only the list left every successful write invisible.
      // Approve Change wrote the new phase and cleared the recommendation, the
      // banner stayed exactly where it was, and the button looked dead.
      setSelectedPatient(prev => prev ? (patientList.find(p => p.id === prev.id) || prev) : prev);
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1>NM Rebuild</h1>
          <button className="mobile-close" onClick={() => setMobileMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>
        
        <div className="sidebar-user">
          <div className="user-avatar">
            {userProfile?.name?.charAt(0) || 'C'}
          </div>
          <div className="user-info">
            <span className="user-name">{userProfile?.name}</span>
            <span className="user-role">{userProfile?.credentials}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${view === 'patients' ? 'active' : ''}`}
            onClick={() => { setView('patients'); setSelectedPatient(null); setMobileMenuOpen(false); }}
          >
            <Users size={20} />
            <span>Patients</span>
            <span className="nav-badge">{patients.length}</span>
          </button>
          
          <button 
            className={`nav-item ${view === 'tools' ? 'active' : ''}`}
            onClick={() => { setView('tools'); setMobileMenuOpen(false); }}
          >
            <Clipboard size={20} />
            <span>Clinical Tools</span>
          </button>
          
          <button 
            className={`nav-item ${view === 'settings' ? 'active' : ''}`}
            onClick={() => { setView('settings'); setMobileMenuOpen(false); }}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="main-header">
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
            <Menu size={24} />
          </button>
          <h2>
            {view === 'patients' && !selectedPatient && 'Patients'}
            {view === 'patients' && selectedPatient && selectedPatient.displayName}
            {view === 'tools' && 'Clinical Tools'}
            {view === 'settings' && 'Settings'}
          </h2>
          {view === 'patients' && !selectedPatient && (
            <button className="header-btn primary" onClick={() => setShowAddPatient(true)}>
              <Plus size={18} />
              <span>Add Patient</span>
            </button>
          )}
          {selectedPatient && (
            <button className="header-btn" onClick={() => setSelectedPatient(null)}>
              <ArrowLeft size={18} />
              <span>Back</span>
            </button>
          )}
        </header>

        <div className="main-body">
          {loading ? (
            <LoadingScreen />
          ) : (
            <>
              {view === 'patients' && !selectedPatient && (
                <PatientList 
                  patients={patients} 
                  onSelect={setSelectedPatient}
                />
              )}
              
              {view === 'patients' && selectedPatient && (
                <PatientDetail 
                  patient={selectedPatient}
                  onUpdate={() => loadPatients()}
                  onDelete={() => {
                    setSelectedPatient(null);
                    loadPatients();
                  }}
                />
              )}
              
              {view === 'tools' && (
                <ClinicalTools />
              )}
              
              {view === 'settings' && (
                <ClinicianSettings />
              )}
            </>
          )}
        </div>
      </main>

      {/* Add Patient Modal */}
      {showAddPatient && (
        <AddPatientModal 
          onClose={() => setShowAddPatient(false)}
          onSuccess={() => {
            setShowAddPatient(false);
            loadPatients();
          }}
          clinicianId={userProfile.uid}
          clinicianName={userProfile.name}
        />
      )}
    </div>
  );
}

// ============================================
// PATIENT LIST
// ============================================

function PatientList({ patients, onSelect }) {
  const [search, setSearch] = useState('');

  const filtered = patients.filter(p => 
    p.displayName?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getPhaseInfo = (phase) => PHASES[phase] || PHASES.stabilization;

  return (
    <div className="patient-list">
      <div className="list-header">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search patients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No patients yet</h3>
          <p>Add your first patient to get started</p>
        </div>
      ) : (
        <div className="patient-cards">
          {filtered.map(patient => {
            const phase = getPhaseInfo(patient.phase);
            return (
              <div 
                key={patient.id} 
                className="patient-card"
                onClick={() => onSelect(patient)}
              >
                <div className="patient-avatar">
                  {patient.displayName?.charAt(0) || 'P'}
                </div>
                <div className="patient-info">
                  <h4>{patient.displayName}</h4>
                  <p>{patient.email}</p>
                </div>
                <div 
                  className="patient-phase"
                  style={{ backgroundColor: phase.color + '20', color: phase.color }}
                >
                  {phase.name}
                </div>
                <div className={`patient-status ${patient.status}`}>
                  {patient.status === 'active' ? (
                    <CheckCircle size={16} />
                  ) : (
                    <XCircle size={16} />
                  )}
                </div>
                <ChevronRight className="chevron" size={20} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// PATIENT DETAIL
// ============================================

// ============================================================================
// ANALYZER PROFILE — v4 and before
//
// nm-analyzer v4 reports eight named sections in `sectionProfile`. Records
// written before v4 carry five gates in `gateProfile` instead, and those
// patients still have pending recommendations, so both are rendered.
//
// Section keys are words on purpose: R1-R7 are the Residents (the Gatekeeper
// is not one of them — there is no R8), Stages 1-6 and Gates 7-10 are the
// governance components, and G1-G5 in pre-v4 records are the Analyzer's own
// legacy gate profile, not governance gates. A section is none of those and
// must not borrow their notation.
// ============================================================================

// The Analyzer names a phase in words; Rebuild keys them as slugs.
const PHASE_SLUG = {
  'STABILIZATION': 'stabilization',
  'SOFT ARTICULATION': 'soft_articulation',
  'DIRECTION RECOVERY': 'direction_recovery',
  'PROCESS RESUMPTION': 'process_resumption'
};

const SECTION_LABELS = {
  ground: "Ground",
  capacity: "Capacity",
  house: "House",
  alignment: "Alignment",
  loop: "Loop",
  direction: "Direction",
  uncertainty: "Uncertainty",
  articulation: "Articulation"
};

// [label, status] pairs, whichever shape the record was written in.
const profileEntries = (rec) => {
  if (!rec) return [];
  if (rec.sectionProfile) {
    return Object.keys(SECTION_LABELS)
      .filter(k => rec.sectionProfile[k])
      .map(k => [SECTION_LABELS[k], rec.sectionProfile[k]]);
  }
  if (rec.gateProfile) {
    return Object.entries(rec.gateProfile).map(([g, s]) => [g.replace("gate", "G"), s]);
  }
  return [];
};

// A labelled list, or nothing at all when the list is empty.
const reportList = (label, items, color = INK.body) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '11px', color: INK.muted, marginBottom: '4px',
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: '18px', color, fontSize: '13px', lineHeight: 1.6 }}>
        {items.map((x, i) => <li key={i}>{typeof x === 'string' ? x : JSON.stringify(x)}</li>)}
      </ul>
    </div>
  );
};

// ============================================================================
// PAPER
//
// A clinical reading is a document, not application chrome. It is shown on
// white with dark text — the way the Analyzer's own report is read, and the
// only way it survives being printed. The dark shell stays dark; what sits on
// it is paper. Defined once so the surfaces cannot drift apart.
// ============================================================================

const PAPER = {
  background: '#ffffff',
  color: '#111827',
  border: '1px solid #e2e8f0',
  borderRadius: '10px'
};

const INK = {
  strong: '#111827',   // headings, the state class, anything read first
  body:   '#1f2937',   // prose
  muted:  '#6b7280',   // labels, rationale
  faint:  '#9ca3af',   // provenance, timestamps
  rule:   '#e2e8f0'
};

// Status colours for light ground. The dark-ground versions were unreadable
// against white: bright amber on pale amber has almost no contrast.
const STATUS_COLOR = {
  RED:    { fg: "#991b1b", bg: "#fee2e2", br: "#fca5a5" },
  YELLOW: { fg: "#854d0e", bg: "#fef3c7", br: "#fcd34d" },
  GREEN:  { fg: "#166534", bg: "#dcfce7", br: "#86efac" }
};

const statusChipStyle = (status) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.GREEN;
  return {
    padding: "3px 9px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${c.br}`
  };
};

// The body of a session report. One renderer, two origins: the report the
// Cloud Function drafts automatically, and the one a clinician generates from
// the chart. A saved note reads identically whichever produced it.
// ============================================================================
// THE DETERMINISTIC HALF
//
// Rendered straight from the stored payload by code. No language model touches
// it, so it cannot be elaborated, graded, averaged or causally linked — the
// four ways an AI restating a deterministic reading goes wrong while sounding
// more expert, not less. Dr. Oyelese, 2026-09-26.
// ============================================================================

function AnalyzerReading({ data }) {
  if (!data?.sectionProfile) {
    return <div style={{ color: INK.muted, fontSize: '13px' }}>
      No Analyzer assessment on record for this chart.
    </div>;
  }
  const line = { color: INK.body, fontSize: '12px', lineHeight: 1.9 };
  return (
    <div>
      {data.bofOutput?.class && (
        <div style={{ color: INK.strong, fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
          {data.bofOutput.class}
          {data.bofOutput.flag && (
            <span style={{ color: '#991b1b', fontWeight: 700 }}> ⚠ {data.bofOutput.flag}</span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {profileEntries(data).map(([label, status]) => (
          <span key={label} style={statusChipStyle(status)}>{label}: {status}</span>
        ))}
      </div>
      <div style={line}>
        {data.anchors && (
          <>Anchors · {data.anchors.holding} of 4 holding · ground {data.anchors.ground}<br /></>
        )}
        {data.house && (
          <>House unanswered · {Object.entries(data.house).filter(([, v]) => !v).map(([k]) => k).join(', ') || 'none'}<br /></>
        )}
        {data.loop && (
          <>Loop not running · {Object.entries(data.loop).filter(([, v]) => !v).map(([k]) => k).join(', ') || 'all stages running'}<br /></>
        )}
        {data.friction?.length > 0 && (
          <>Live friction · {data.friction.map(f => (typeof f === 'string' ? f : f.pair)).join(' · ')}<br /></>
        )}
        {data.sentinels?.length > 0 && (
          <span style={{ color: '#991b1b', fontWeight: 600 }}>
            ⚠ Sentinel · {data.sentinels.join(', ')}<br />
          </span>
        )}
        <>Analyzer phase · {data.phase || 'not recorded'}</>
        {data.assessmentCode && (
          <><br /><span style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                                 letterSpacing: '0.08em', color: INK.faint }}>
            {data.assessmentCode}
          </span></>
        )}
      </div>
    </div>
  );
}

// Movement between two assessments. Computed, not interpreted: every observable
// has an end that is normal, so direction follows from the reading. A mixed
// picture stays mixed — nothing is netted off into a score.
function MovementReading({ prev, curr }) {
  const m = compareAssessments(prev, curr);
  if (!m.ok) {
    return <div style={{ color: INK.muted, fontSize: '13px' }}>{m.error}</div>;
  }
  const tone = { IMPROVEMENT:'#166534', WORSENING:'#991b1b', MIXED:'#854d0e', 'NO CHANGE':INK.muted };
  const list = (rows, colour, label) => rows.length === 0 ? null : (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', color: INK.muted, marginBottom: '4px',
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: '18px', color: colour, fontSize: '12.5px', lineHeight: 1.7 }}>
        {rows.map((r, i) => (
          <li key={i}>{r.what} <span style={{ color: INK.muted }}>{r.from} → {r.to}</span></li>
        ))}
      </ul>
    </div>
  );
  return (
    <div>
      <div style={{ color: tone[m.direction], fontSize: '15px', fontWeight: 700, marginBottom: '2px' }}>
        {m.direction}
      </div>
      <div style={{ color: INK.muted, fontSize: '12px', marginBottom: '10px' }}>
        {m.moved} observation{m.moved === 1 ? '' : 's'} moved · {m.unchanged} unchanged ·
        {' '}phase {m.phase.from} → {m.phase.to} ({m.phase.direction})
      </div>
      {list(m.improved, '#166534', 'Toward normalcy')}
      {list(m.worsened, '#991b1b', 'Away from normalcy')}
      {list(m.changed, INK.body, 'Changed, direction not ranked')}
      <div style={{ color: INK.faint, fontSize: '11px' }}>
        Correlation only. This says what changed between two readings, never what produced it.
      </div>
    </div>
  );
}

const SECTION_HEADING = (kind) => ({
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: kind === 'det' ? '#166534' : '#1d4ed8',
  borderTop: `2px solid ${kind === 'det' ? '#86efac' : '#93c5fd'}`,
  paddingTop: '8px', marginTop: '18px', marginBottom: '8px'
});

function SessionReportBody({ r }) {
  return (
    <>
      <div style={SECTION_HEADING('det')}>
        Deterministic · NM Analyzer
      </div>
      <div style={{ marginBottom: '4px', padding: '12px 14px',
                    background: '#f8fafc', border: `1px solid ${INK.rule}`,
                    borderRadius: '8px' }}>
        <AnalyzerReading data={r.analyzerPayload} />
      </div>
      {r.prevAnalyzerPayload && (
        <div style={{ marginTop: '10px', padding: '12px 14px',
                      background: '#f8fafc', border: `1px solid ${INK.rule}`,
                      borderRadius: '8px' }}>
          <div style={{ fontSize: '11px', color: INK.muted, marginBottom: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Movement since the previous assessment
          </div>
          <MovementReading prev={r.prevAnalyzerPayload} curr={r.analyzerPayload} />
        </div>
      )}
      <p style={{ color: INK.faint, fontSize: '11px', margin: '4px 0 4px 0' }}>
        Rendered from the assessment itself. No AI touched this section.
      </p>

      <div style={SECTION_HEADING('prob')}>
        Probabilistic · AI Clinical Coordinator
      </div>
      <p style={{ color: INK.faint, fontSize: '11px', margin: '0 0 10px 0' }}>
        Inference over the sequence. Drafts for you to weigh — not findings.
      </p>

      {r.continuity && (
        <p style={{ margin: '0 0 12px 0', color: INK.body, fontSize: '14px', lineHeight: 1.6 }}>
          {r.continuity}
        </p>
      )}

      {reportList('Moved', r.moved, '#854d0e')}
      {reportList('Unchanged', r.unchanged, INK.muted)}

      {/* Under a tragic fork NMA does not recommend, and this is enforced here
          rather than only asked of the model. Dr. Oyelese, 2026-09-26: "the way
          to address a tragic fork is to articulate the situation without any
          recommendations for resolution ... if there is a clear path it is NOT
          a Tragic Fork." */}
      {r.analyzerPayload?.bofOutput?.class === 'TRAGIC_FORK' ? (
        <div style={{ marginBottom: '12px', padding: '12px 14px',
                      background: '#faf5ff', border: '1px solid #d8b4fe',
                      borderRadius: '8px' }}>
          <div style={{ fontSize: '11px', color: '#6b21a8', marginBottom: '6px',
                        fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            No recommendations — tragic fork
          </div>
          <div style={{ color: INK.body, fontSize: '13px', lineHeight: 1.7 }}>
            P(D) is unreachable for every party, on every path. There is no path to
            recommend, and if a clear path existed this would not be a tragic fork.
            What is called for instead is <strong>articulation</strong>: laying the
            situation out fully, so that all parties can see it, without blame.
          </div>
        </div>
      ) : r.recommendations?.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: INK.muted, marginBottom: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Recommendations — drafts for you to weigh
          </div>
          {/* `rec`, not `r`: the map used to shadow the report itself. */}
          {r.recommendations.map((rec, i) => (
            <div key={i} style={{ marginBottom: '8px', paddingLeft: '10px',
                                  borderLeft: '2px solid #93c5fd' }}>
              <div style={{ color: INK.strong, fontSize: '13px' }}>{rec.suggestion}</div>
              {rec.rationale && (
                <div style={{ color: INK.muted, fontSize: '12px' }}>{rec.rationale}</div>
              )}
              {rec.grounded_in && (
                <div style={{ color: INK.faint, fontSize: '11px', fontStyle: 'italic' }}>
                  Grounded in: {rec.grounded_in}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {r.phaseObservation && (
        <p style={{ margin: '0 0 12px 0', color: INK.body, fontSize: '13px' }}>
          <strong style={{ color: INK.strong }}>Phase observation:</strong>{' '}
          {r.phaseObservation}
          <span style={{ color: INK.muted }}> — an observation, not a phase change.</span>
        </p>
      )}

      {reportList('Questions for you', r.questionsForClinician, '#1d4ed8')}
      {reportList('Not established by this report', r.notEstablished, INK.muted)}
    </>
  );
}

// The generator returns snake_case; the stored record is camelCase. One place
// converts, so the shape cannot drift between the two paths.
const normalizeReport = (raw) => ({
  analyzerState: raw.analyzer_state || null,
  continuity: raw.continuity || '',
  unchanged: raw.unchanged || [],
  moved: raw.moved || [],
  recommendations: raw.recommendations || [],
  questionsForClinician: raw.questions_for_clinician || [],
  notEstablished: raw.not_established || [],
  phaseObservation: raw.phase_observation || null,
  confidence: raw.confidence || 'LOW'
});

function PatientDetail({ patient, onUpdate, onDelete }) {
  // who is approving — the saved report carries their name
  const { user, userProfile } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [showPhaseChange, setShowPhaseChange] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showToolConfig, setShowToolConfig] = useState(false);
  const [visibleTools, setVisibleTools] = useState(patient.visibleTools || Object.keys(TOOLS).filter(k => !TOOLS[k].clinicianOnly));

  useEffect(() => {
    loadActivities();
  }, [patient.id]);

  useEffect(() => {
    // Update visible tools when patient changes
    setVisibleTools(patient.visibleTools || Object.keys(TOOLS).filter(k => !TOOLS[k].clinicianOnly));
  }, [patient.id, patient.visibleTools]);

  const loadActivities = async () => {
    try {
      const q = query(
        collection(db, 'activities'),
        where('patientId', '==', patient.id),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const activityList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActivities(activityList);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  };

  const deletePatient = async () => {
    try {
      await deleteDoc(doc(db, 'users', patient.id));
      onDelete();
    } catch (err) {
      console.error('Error deleting patient:', err);
      alert('Failed to delete patient. Please try again.');
    }
  };

  const changePhase = async (newPhase) => {
    try {
      await updateDoc(doc(db, 'users', patient.id), {
        phase: newPhase,
        phaseStartedAt: serverTimestamp(),
        pendingPhaseRecommendation: null // Clear recommendation when phase is changed
      });
      setShowPhaseChange(false);
      onUpdate();
    } catch (err) {
      console.error('Error changing phase:', err);
    }
  };

  const toggleToolVisibility = (toolKey) => {
    setVisibleTools(prev => {
      if (prev.includes(toolKey)) {
        return prev.filter(k => k !== toolKey);
      } else {
        return [...prev, toolKey];
      }
    });
  };

  const saveToolVisibility = async () => {
    try {
      await updateDoc(doc(db, 'users', patient.id), {
        visibleTools
      });
      setShowToolConfig(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving tool visibility:', err);
      alert('Failed to save tool configuration.');
    }
  };

  const approvePhaseRecommendation = async () => {
    if (!patient.pendingPhaseRecommendation) return;
    await changePhase(patient.pendingPhaseRecommendation.recommendedPhase);
  };

  // Remove an assessment entered twice or against the wrong chart. Deletion is
  // permanent — Firestore has no undo — and an assessment is a point in the
  // sequence that progression is computed from. So the risk is not deleting;
  // it is deleting the WRONG one. The confirmation therefore names the
  // assessment rather than asking "are you sure?".
  const deleteAssessment = async (activity) => {
    const d = activity.data || {};
    const when = activity.timestamp?.toDate?.().toLocaleString() || 'undated';
    const what = [
      d.bofOutput?.class,
      d.phase,
      d.assessmentCode
    ].filter(Boolean).join(' \u00b7 ');

    // A session report drafted against this assessment documents a reading of
    // something that will no longer exist, so it goes with it. Notes saved
    // before the link was recorded carry no assessmentActivityId and are left
    // alone — the rules will not delete what they cannot verify.
    const dependentNotes = notes.filter(n =>
      n.assessmentActivityId === activity.id || n.prevAssessmentActivityId === activity.id);

    const alsoGoing = dependentNotes.length
      ? `\n\n${dependentNotes.length} saved session report` +
        `${dependentNotes.length === 1 ? '' : 's'} drafted against it will be ` +
        `removed too \u2014 a report of a reading that no longer exists is not a record.`
      : '';

    if (!window.confirm(
      `Remove this assessment from ${patient.displayName}'s chart?\n\n` +
      `${when}\n${what || 'no reading recorded'}` + alsoGoing + `\n\n` +
      `This cannot be undone. Movement between assessments is recalculated ` +
      `from what remains.`
    )) return;

    try {
      // Order matters, and the rules enforce it: the assessment must be gone
      // before a note that rests on it becomes deletable.
      await deleteDoc(doc(db, 'activities', activity.id));

      for (const n of dependentNotes) {
        try { await deleteDoc(doc(db, 'sessionNotes', n.id)); }
        catch (e) { console.error('Could not remove dependent note', n.id, e); }
      }

      await loadActivities();
      await loadNotes();
      onUpdate();
    } catch (err) {
      console.error('Could not remove the assessment:', err);
      alert('Could not remove that assessment. ' + (err.code || err.message || ''));
    }
  };

  // ---- Past sessions -----------------------------------------------------
  // Approved reports were being written to sessionNotes and never read back.
  // They are the record; a record you cannot open is not one.
  const [notes, setNotes] = useState([]);
  const [openNote, setOpenNote] = useState(null);

  const loadNotes = async () => {
    try {
      const q = query(collection(db, 'sessionNotes'),
                      where('patientId', '==', patient.id),
                      orderBy('approvedAt', 'desc'));
      const snap = await getDocs(q);
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Could not load past sessions:', err);
    }
  };

  useEffect(() => { loadNotes(); /* eslint-disable-next-line */ }, [patient.id]);

  // ---- Assessment code ---------------------------------------------------
  // The Analyzer prints an eight-character code carrying all 39 answers. This
  // does not copy its reading: the same engine runs here on the same answers
  // and reaches the same result. Nothing is interpreted, and no AI is involved.
  //
  // The decoded result is written as an `activities` record in exactly the
  // shape the live handshake produces, so everything downstream — including the
  // continuity report — cannot tell the two routes apart.
  const [code, setCode] = useState('');
  const [codePreview, setCodePreview] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [codeSaving, setCodeSaving] = useState(false);

  const readCode = () => {
    setCodeError(null);
    setCodePreview(null);
    if (!code.trim()) {
      setCodeError('Enter the assessment code printed on the Analyzer report — it looks like NMA4-XXXXXXXX-C.');
      return;
    }
    const got = readAssessmentCode(code, Date.now());
    if (!got.ok) { setCodeError(got.error); return; }
    setCodePreview(got);
  };

  const addCodeToChart = async () => {
    if (!codePreview) return;
    setCodeSaving(true);
    try {
      await addDoc(collection(db, 'activities'), {
        patientId: patient.id,
        tool: 'nm-analyzer',
        event: 'assessment_completed',
        source: 'assessment-code',
        enteredBy: user?.uid || null,
        data: codePreview.payload,
        timestamp: serverTimestamp()
      });
      // Progression follows the Analyzer, every time, forwards or backwards.
      // Dr. Oyelese, 2026-09-26: "the phase is determined by the analyzer
      // results and goes back and forth if necessary." The Analyzer is an
      // instrument, not the AI: it determines state, so the phase is APPLIED
      // rather than proposed. The clinician is told, not asked — and can
      // override at any time with Change Phase.
      const slug = PHASE_SLUG[codePreview.payload.phase];
      if (slug && slug !== patient.phase) {
        const back = (PHASES[slug]?.order || 0) < (PHASES[patient.phase]?.order || 0);
        await updateDoc(doc(db, 'users', patient.id), {
          phase: slug,
          phaseStartedAt: serverTimestamp(),
          pendingPhaseRecommendation: null,
          lastPhaseChange: {
            from: patient.phase || 'unassessed',
            to: slug,
            by: 'analyzer',
            source: 'assessment-code',
            direction: back ? 'REGRESS' : 'ADVANCE',
            reason: back
              ? `The assessment reads ${codePreview.payload.phase}. Regression is permitted — `
                + 'collapse is structural, not moral.'
              : `The assessment reads ${codePreview.payload.phase}.`,
            ...codePreview.payload,
            at: new Date()
          }
        });
      }

      setCode('');
      setCodePreview(null);
      await loadActivities();
      onUpdate();
    } catch (err) {
      console.error('Could not add the assessment:', err);
      setCodeError('Could not add that assessment to the chart.');
    } finally {
      setCodeSaving(false);
    }
  };

  // Printing opens a window whose entire content IS the report, and prints
  // that. The previous approach hid the application with `visibility: hidden`
  // and lifted the report out with `position: absolute` — which printed one
  // blank page, because an absolutely-positioned element inside a long hidden
  // document does not reliably land on the printed page. The Analyzer has
  // never had this problem for the same reason: there, the page is the report.
  // Remove a saved session report. The confirmation names it — the failure
  // mode is removing the wrong one, not removing at all.
  const deleteSavedNote = async (n) => {
    const when = n.approvedAt?.toDate?.().toLocaleString() || 'saved';
    const ph = PHASES[n.phaseAtTimeOfReport]?.name || n.phaseAtTimeOfReport || '\u2014';
    if (!window.confirm(
      `Delete this session report from ${patient.displayName}'s record?\n\n` +
      `Approved ${when}${n.approvedByName ? ' by ' + n.approvedByName : ''}\n` +
      `Phase ${ph} \u00b7 confidence ${n.confidence || '\u2014'}\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      await deleteDoc(doc(db, 'sessionNotes', n.id));
      setOpenNote(null);
      await loadNotes();
    } catch (err) {
      console.error('Could not delete the session report:', err);
      alert('Could not delete that report. ' + (err.code || err.message || ''));
    }
  };

  const printSaved = (n) => printNode(`saved-report-${n.id}`);
  const printReport = () => printNode('session-report-print');

  const printNode = (elementId) => {
    const node = document.getElementById(elementId);
    if (!node) return;

    const clone = node.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach(el => el.remove());

    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) { setDraftError('The browser blocked the print window. Allow pop-ups for this site.'); return; }

    w.document.write(`<!doctype html><html><head>
      <meta charset="utf-8">
      <title>Session Report — ${(patient.displayName || 'Patient').replace(/[<>]/g, '')}</title>
      <style>
        /* The screen palette is light-on-dark; paper is the other way round,
           so every inherited colour and background is overridden here. */
        * { color: #111827 !important; background: transparent !important;
            border-color: #d1d5db !important; box-sizing: border-box; }
        body { font: 12pt/1.6 ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
               margin: 0; padding: 0; }
        h4 { font-size: 15pt; margin: 0 0 4pt; }
        ul { margin: 0 0 6pt 18pt; padding: 0; }
        li, p, div { break-inside: avoid; }
        @page { margin: 16mm; }
      </style></head><body>${clone.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    // Give the new document a tick to lay out before the dialog opens.
    setTimeout(() => { w.print(); }, 250);
  };

  // The session report a clinician generates from the chart, as against the one
  // the Cloud Function drafts on activity. Same shape, same save path.
  const [openActivity, setOpenActivity] = useState(null);
  const [draftReport, setDraftReport] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState(null);

  const generateSessionReport = async () => {
    setDraftLoading(true);
    setDraftError(null);
    try {
      const [documents, convSnap] = await Promise.all([
        listDocuments(patient.id).catch(() => []),
        getDocs(query(collection(db, 'conversations'),
                      where('patientId', '==', patient.id),
                      orderBy('createdAt', 'asc'))).catch(() => null)
      ]);
      const conversation = convSnap
        ? convSnap.docs.map(d => ({ role: d.data().role, content: d.data().content }))
        : [];

      // The deterministic half of the report is rendered from this, by code.
      // `activities` arrives newest-first, so the first assessment is the latest.
      const assessments = activities.filter(a => a.data?.sectionProfile);
      const analyzerPayload = assessments[0]?.data || null;
      const prevAnalyzerPayload = assessments[1]?.data || null;
      // The activity this report is drafted against. Stored on the saved note so
      // the note can be removed with it, and so the rules can VERIFY the basis
      // has gone rather than trusting the client.
      const assessmentActivityId = assessments[0]?.id || null;
      const prevAssessmentActivityId = assessments[1]?.id || null;

      const { report } = await aiService.generateSessionReport(patient, activities,
                                                              { documents, conversation });
      setDraftReport({
        ...normalizeReport(report),
        analyzerPayload,
        prevAnalyzerPayload,
        assessmentActivityId,
        prevAssessmentActivityId,
        assessmentsOnRecord: assessments.length,
        documentsConsidered: documents.length,
        conversationTurns: conversation.length,
        activitiesConsidered: activities.length
      });
    } catch (err) {
      console.error('Could not generate the session report:', err);
      setDraftError(err.message || 'Could not generate the report.');
    } finally {
      setDraftLoading(false);
    }
  };

  // Approving is what puts it in the record. Same collection, same create-only
  // rule, as the automatic report — the origin is recorded, not the authority.
  const saveDraftReport = async () => {
    if (!draftReport) return;
    try {
      await addDoc(collection(db, 'sessionNotes'), {
        ...draftReport,
        patientId: patient.id,
        source: 'clinician-generated',
        phaseAtTimeOfReport: patient.phase,
        status: 'approved',
        approvedBy: user?.uid || null,
        approvedByName: userProfile?.displayName || null,
        approvedAt: serverTimestamp()
      });
      setDraftReport(null);
      await loadNotes();
      onUpdate();
    } catch (err) {
      console.error('Could not save the report:', err);
      setDraftError('Could not save the report to the record.');
    }
  };

  // The AI's report is a draft. Approving it saves it to the record under the
  // clinician's name; it enacts nothing, and it never touches phase.
  const approveSessionReport = async () => {
    const report = patient.pendingSessionReport;
    if (!report) return;
    try {
      await addDoc(collection(db, 'sessionNotes'), {
        ...report,
        patientId: patient.id,
        status: 'approved',
        approvedBy: user?.uid || null,
        approvedByName: userProfile?.displayName || null,
        approvedAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'users', patient.id), { pendingSessionReport: null });
      await loadNotes();
      onUpdate();
    } catch (err) {
      console.error('Error approving session report:', err);
      alert('Failed to save the session report.');
    }
  };

  const dismissSessionReport = async () => {
    try {
      await updateDoc(doc(db, 'users', patient.id), { pendingSessionReport: null });
      onUpdate();
    } catch (err) {
      console.error('Error dismissing session report:', err);
    }
  };

  const dismissPhaseRecommendation = async () => {
    try {
      // Dismiss clears the notice, not the phase. The phase the Analyzer
      // determined stands until a clinician changes it with Change Phase.
      await updateDoc(doc(db, 'users', patient.id), {
        pendingPhaseRecommendation: null,
        lastPhaseChange: null
      });
      onUpdate();
    } catch (err) {
      console.error('Error dismissing recommendation:', err);
    }
  };

  const phase = PHASES[patient.phase] || PHASES.unassessed;
  const patientTools = Object.entries(TOOLS).filter(([key, tool]) => 
    !tool.clinicianOnly && visibleTools.includes(key)
  );
  const clinicianTools = Object.entries(TOOLS).filter(([key, tool]) => tool.clinicianOnly);

  const launchTool = (tool, toolKey) => {
    // Generate a rebuild token with patient demographics
    const token = btoa(JSON.stringify({
      patientId: patient.id,
      tool: toolKey,
      timestamp: Date.now(),
      // Patient demographics for tools like Analyzer
      patientInfo: {
        surname:   patient.surname   || '',
        firstName: patient.firstName || '',
        name:      patient.displayName || '',   // for a patient created before the change
        dob: patient.dob || '',
        sex: patient.sex || ''
      }
    }));
    const url = `${tool.url}?rebuild_token=${token}`;
    window.open(url, '_blank');
  };

  const recommendation = patient.lastPhaseChange || patient.pendingPhaseRecommendation;
  const sessionReport = patient.pendingSessionReport;
  const recommendedPhase = recommendation
    ? PHASES[recommendation.to || recommendation.recommendedPhase] : null;

  return (
    <div className="patient-detail">
      {/* Phase Recommendation Alert */}
      {recommendation && (
        <div style={{
          padding: '18px 22px', marginBottom: '20px', ...PAPER,
          borderLeft: '4px solid #d97706'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <AlertTriangle size={24} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 4px 0', color: INK.strong, fontSize: '17px' }}>
                {patient.lastPhaseChange ? 'Phase updated by the Analyzer'
                                          : 'Phase Recommendation from Analyzer'}
              </h4>
              <p style={{ margin: '0 0 8px 0', color: INK.body, fontSize: '14px' }}>
                {patient.lastPhaseChange ? (
                  <>The assessment moved this patient{' '}
                    {recommendation.from && PHASES[recommendation.from]
                      ? <>from <strong>{PHASES[recommendation.from].name}</strong>{' '}</> : null}
                    to <strong style={{ color: recommendedPhase?.color }}>{recommendedPhase?.name}</strong>
                    {recommendation.direction === 'REGRESS' && (
                      <span style={{ color: '#854d0e' }}> — a regression, which is correction, not failure.</span>
                    )}
                  </>
                ) : (
                  <>Based on the latest assessment, consider moving this patient to{' '}
                    <strong style={{ color: recommendedPhase?.color }}>{recommendedPhase?.name}</strong></>
                )}
              </p>
              <p style={{ margin: '0 0 12px 0', color: INK.muted, fontSize: '13px' }}>
                Reason: {recommendation.reason}
              </p>
              {recommendation.bofOutput?.class && (
                <p style={{ margin: '0 0 10px 0', color: INK.body, fontSize: '14px' }}>
                  Output class: <strong style={{ color: INK.strong }}>{recommendation.bofOutput.class}</strong>
                  {recommendation.bofOutput.flag && (
                    <span style={{ color: '#991b1b', fontWeight: 600 }}> ⚠ {recommendation.bofOutput.flag}</span>
                  )}
                </p>
              )}

              {recommendation.sentinels?.length > 0 && (
                <p style={{ margin: '0 0 10px 0', color: '#991b1b', fontSize: '13px', fontWeight: 600 }}>
                  ⚠ Sentinel item{recommendation.sentinels.length > 1 ? 's' : ''} fired — phase forced to Stabilization
                </p>
              )}

              {profileEntries(recommendation).length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: INK.muted, marginBottom: '6px',
                                textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {recommendation.sectionProfile ? 'Section profile' : 'Gate profile (pre-v4)'}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {profileEntries(recommendation).map(([label, status]) => (
                      <span key={label} style={statusChipStyle(status)}>{label}: {status}</span>
                    ))}
                  </div>
                </div>
              )}


              {recommendation.friction?.length > 0 && (
                <p style={{ margin: '0 0 12px 0', color: INK.muted, fontSize: '13px' }}>
                  Live friction: {recommendation.friction
                    .map(f => (typeof f === 'string' ? f : f.pair)).join(' · ')}
                </p>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                {!patient.lastPhaseChange && <button 
                  onClick={approvePhaseRecommendation}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#22c55e',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <CheckCircle size={16} /> Approve Change
                </button>}
                <button 
                  onClick={dismissPhaseRecommendation}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    color: '#475569',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <X size={16} /> {patient.lastPhaseChange ? 'Acknowledge' : 'Dismiss'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Session Report — a draft for the clinician. Approving saves it;
           it changes no state, and the Analyzer's result is quoted, not revised. */}
      {sessionReport && (
        <div style={{
          padding: '18px 22px', marginBottom: '20px', ...PAPER,
          borderLeft: '4px solid #2563eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <FileText size={22} style={{ color: '#2563eb', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{ margin: '0 0 2px 0', color: INK.strong, fontSize: '17px' }}>
                Session Report — awaiting your approval
              </h4>
              <p style={{ margin: '0 0 14px 0', color: INK.faint, fontSize: '12px' }}>
                Drafted by the AI Clinical Coordinator from {sessionReport.assessmentsConsidered || 0}{' '}
                assessment{sessionReport.assessmentsConsidered === 1 ? '' : 's'} and{' '}
                {sessionReport.activitiesConsidered || 0} activities · confidence{' '}
                {sessionReport.confidence}
                {sessionReport.libraryInContext === false && ' · Clinical Library not loaded'}
                {' '}· recommends only; decides nothing
              </p>

              <SessionReportBody r={{
                ...sessionReport,
                // The stored record holds the AI's fields; the deterministic
                // half is rendered from the latest assessment in this chart.
                analyzerPayload: sessionReport.analyzerPayload
                  || activities.filter(a => a.data?.sectionProfile)[0]?.data || null,
                prevAnalyzerPayload: sessionReport.prevAnalyzerPayload
                  || activities.filter(a => a.data?.sectionProfile)[1]?.data || null
              }} />

              <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                <button onClick={approveSessionReport} style={{
                  padding: '8px 16px', backgroundColor: '#22c55e', border: 'none',
                  borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  <CheckCircle size={16} /> Approve &amp; Save to Record
                </button>
                <button onClick={dismissSessionReport} style={{
                  padding: '8px 16px', backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1', borderRadius: '8px',
                  color: '#475569', fontWeight: '500', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  <X size={16} /> Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patient Header */}
      <div className="detail-header">
        <div className="patient-avatar large">
          {patient.displayName?.charAt(0) || 'P'}
        </div>
        <div className="patient-header-info">
          <h2>{patient.displayName}</h2>
          <p>{patient.email}</p>
        </div>
        <div 
          className="phase-badge large"
          style={{ backgroundColor: phase.color }}
        >
          {phase.name}
        </div>
      </div>

      {/* Patient Demographics */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Demographics</h3>
          <span style={{ fontSize: '11px', color: '#64748b' }}>From EMR</span>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          padding: '16px',
          backgroundColor: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '12px',
          border: '1px solid rgba(51, 65, 85, 0.5)'
        }}>
          <div>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Date of Birth</div>
            <div style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>
              {patient.dob || 'Not set'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Sex</div>
            <div style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>
              {patient.sex || 'Not set'}
            </div>
          </div>
          
          <div>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Email</div>
            <div style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>
              {patient.email || 'Not set'}
            </div>
          </div>
        </div>
      </div>

      {/* Phase Info */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Current Phase</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="section-btn" onClick={() => setShowPhaseChange(true)}>
              <RefreshCw size={16} /> Change Phase
            </button>
            <button 
              className="section-btn" 
              onClick={() => setShowDeleteConfirm(true)}
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderColor: '#ef4444' }}
            >
              <XCircle size={16} /> Delete Patient
            </button>
          </div>
        </div>
        <div className="phase-card" style={{ borderColor: phase.color }}>
          <div className="phase-info">
            <h4>{phase.name}</h4>
            <p>{phase.description}</p>
          </div>
          <div className="phase-meta">
            <span><Calendar size={14} /> Started: {patient.phaseStartedAt?.toDate?.().toLocaleDateString() || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Patient Tools */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Patient Tools</h3>
          <button 
            className="section-btn" 
            onClick={() => setShowToolConfig(true)}
            title="Configure visible tools"
          >
            <Settings size={16} /> Configure
          </button>
        </div>
        {patientTools.length === 0 ? (
          <div className="empty-state small">
            <Eye size={24} />
            <p>No tools visible to patient</p>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Click Configure to enable tools
            </span>
          </div>
        ) : (
          <div className="tools-grid">
            {patientTools.map(([key, tool]) => {
              const Icon = tool.icon;
              return (
                <button 
                  key={key} 
                  className="tool-card"
                  onClick={() => launchTool(tool, key)}
                >
                  <Icon size={24} />
                  <span className="tool-name">{tool.name}</span>
                  <span className="tool-desc">{tool.description}</span>
                  <ExternalLink size={14} className="tool-external" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Clinician Tools */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Clinician Tools</h3>
        </div>
        <div className="tools-grid">
          {clinicianTools.map(([key, tool]) => {
            const Icon = tool.icon;
            return (
              <button 
                key={key} 
                className="tool-card clinician"
                onClick={() => launchTool(tool, key)}
                style={{ borderColor: 'rgba(168, 85, 247, 0.3)' }}
              >
                <Icon size={24} style={{ color: '#a855f7' }} />
                <span className="tool-name">{tool.name}</span>
                <span className="tool-desc">{tool.description}</span>
                <ExternalLink size={14} className="tool-external" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Activity Dashboard */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Activity Dashboard</h3>
          <button 
            className="section-btn" 
            onClick={loadActivities}
            title="Refresh"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
        
        {loadingActivities ? (
          <div className="loading-inline">Loading activities...</div>
        ) : activities.length === 0 ? (
          <div className="empty-state small">
            <Activity size={24} />
            <p>No activity recorded yet</p>
            <span style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
              Activity will appear here when the patient uses tools from Rebuild
            </span>
          </div>
        ) : (
          <>
            {/* Activity Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                padding: '16px',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
                  {activities.filter(a => a.event === 'session_start').length}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Sessions</div>
              </div>
              <div style={{
                padding: '16px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  {[...new Set(activities.map(a => a.tool))].length}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Tools Used</div>
              </div>
              <div style={{
                padding: '16px',
                background: 'rgba(168, 85, 247, 0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#a855f7' }}>
                  {activities.filter(a => a.event === 'message_sent').length}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Messages</div>
              </div>
              <div style={{
                padding: '16px',
                background: 'rgba(249, 115, 22, 0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#f97316' }}>
                  {activities[0]?.timestamp?.toDate?.().toLocaleDateString() || 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Last Active</div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '15px',
                top: '0',
                bottom: '0',
                width: '2px',
                background: 'linear-gradient(to bottom, #22c55e, #3b82f6, #a855f7)',
                borderRadius: '2px'
              }} />
              
              <div className="activity-timeline">
                {activities.map((activity, index) => {
                  const toolInfo = TOOLS[activity.tool];
                  const Icon = toolInfo?.icon || Activity;
                  
                  // Event-specific colors
                  const eventColors = {
                    session_start: { bg: 'rgba(34, 197, 94, 0.15)', border: '#22c55e', text: '#22c55e' },
                    message_sent: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#3b82f6' },
                    navigation_started: { bg: 'rgba(168, 85, 247, 0.15)', border: '#a855f7', text: '#a855f7' },
                    direction_requested: { bg: 'rgba(249, 115, 22, 0.15)', border: '#f97316', text: '#f97316' },
                    session_saved: { bg: 'rgba(236, 72, 153, 0.15)', border: '#ec4899', text: '#ec4899' }
                  };
                  const colors = eventColors[activity.event] || { bg: 'rgba(100, 116, 139, 0.15)', border: '#64748b', text: '#64748b' };
                  
                  // Format event name
                  const formatEvent = (event) => {
                    return event.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                  };
                  
                  return (
                    <div 
                      key={activity.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        marginBottom: '16px',
                        paddingLeft: '40px',
                        position: 'relative'
                      }}
                    >
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '4px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: colors.border,
                        border: '3px solid #1e293b',
                        zIndex: 1
                      }} />
                      
                      {/* Activity card */}
                      <div style={{
                        flex: 1,
                        padding: '12px 16px',
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}30`,
                        borderRadius: '12px',
                        borderLeft: `3px solid ${colors.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon size={16} style={{ color: colors.text }} />
                            <span style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '14px' }}>
                              {toolInfo?.name || activity.tool}
                            </span>
                          </div>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            {activity.timestamp?.toDate?.().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            padding: '2px 8px',
                            backgroundColor: colors.bg,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '12px',
                            fontSize: '11px',
                            color: colors.text,
                            fontWeight: '500'
                          }}>
                            {formatEvent(activity.event)}
                          </span>
                          {activity.data?.messageCount && (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              • {activity.data.messageCount} messages
                            </span>
                          )}
                          {activity.data?.phase && (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              • Phase: {activity.data.phase}
                            </span>
                          )}
                          {activity.data?.sectionProfile && (
                            <>
                            <button
                              onClick={() => setOpenActivity(openActivity === activity.id ? null : activity.id)}
                              style={{ background: 'none', border: '1px solid rgba(100,181,246,0.4)',
                                       color: '#64b5f6', borderRadius: '10px', padding: '1px 8px',
                                       fontSize: '11px', cursor: 'pointer' }}>
                              {openActivity === activity.id ? 'Hide reading' : 'Show reading'}
                            </button>
                            <button
                              onClick={() => deleteAssessment(activity)}
                              title="Remove this assessment from the chart"
                              style={{ background: 'none', border: '1px solid rgba(239,68,68,0.35)',
                                       color: '#ef4444', borderRadius: '10px', padding: '1px 8px',
                                       fontSize: '11px', cursor: 'pointer' }}>
                              Remove
                            </button>
                            </>
                          )}
                        </div>

                        {/* The assessment itself. The whole reading has always
                            been in activity.data; the row only ever showed the
                            event name, so a completed assessment looked inert. */}
                        {openActivity === activity.id && activity.data?.sectionProfile && (
                          <div style={{ marginTop: '10px', padding: '12px 14px', ...PAPER }}>
                            {activity.data.bofOutput?.class && (
                              <div style={{ color: INK.strong, fontSize: '14px',
                                            fontWeight: 600, marginBottom: '8px' }}>
                                {activity.data.bofOutput.class}
                                {activity.data.bofOutput.flag && (
                                  <span style={{ color: '#991b1b' }}> ⚠ {activity.data.bofOutput.flag}</span>
                                )}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                              {profileEntries(activity.data).map(([label, status]) => (
                                <span key={label} style={statusChipStyle(status)}>{label}: {status}</span>
                              ))}
                            </div>
                            <div style={{ color: INK.body, fontSize: '12px', lineHeight: 1.8 }}>
                              {activity.data.bofOutput?.class && (
                                <>BOF {activity.data.bofOutput.class}
                                  {activity.data.bofOutput.flag ? ` · ${activity.data.bofOutput.flag}` : ''}<br /></>
                              )}
                              {activity.data.anchors && (
                                <>Anchors · {activity.data.anchors.holding} of 4 holding ·
                                  {' '}ground {activity.data.anchors.ground}<br /></>
                              )}
                              {activity.data.sentinels?.length > 0 && (
                                <span style={{ color: '#991b1b', fontWeight: 600 }}>
                                  ⚠ Sentinel · {activity.data.sentinels.join(', ')}<br />
                                </span>
                              )}
                              {activity.data.friction?.length > 0 && (
                                <>Live friction · {activity.data.friction
                                  .map(f => (typeof f === 'string' ? f : f.pair)).join(' · ')}<br /></>
                              )}
                              {activity.data.loop && Object.values(activity.data.loop).some(v => !v) && (
                                <>Loop not running · {Object.entries(activity.data.loop)
                                  .filter(([, v]) => !v).map(([k]) => k).join(', ')}<br /></>
                              )}
                              {activity.data.assessmentCode && (
                                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                                               letterSpacing: '0.08em', color: INK.muted }}>
                                  {activity.data.assessmentCode}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                          {activity.timestamp?.toDate?.().toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Clinical Documents */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Clinical Documents</h3>
          <span style={{ fontSize: '11px', color: '#a855f7' }}>AI Extraction</span>
        </div>
        <PDFUploadPanel patientId={patient.id} />
      </div>

      {/* Past sessions — the approved record, readable. Write-once: these
          cannot be edited or deleted, by ruling. */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Past Sessions</h3>
          <span style={{ fontSize: '11px', color: '#64748b' }}>
            {notes.length === 0 ? 'none yet'
              : `${notes.length} approved · never edited`}
          </span>
        </div>

        {notes.length === 0 ? (
          <p style={{ color: '#64748b', fontSize: '12px' }}>
            A session report appears here once you approve and save it.
          </p>
        ) : notes.map(n => (
          <div key={n.id} style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: '8px',
                          background: 'rgba(15,23,42,0.4)',
                          border: '1px solid rgba(100,116,139,0.25)' }}>
              <FileText size={16} style={{ color: '#64b5f6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#e2e8f0', fontSize: '13px' }}>
                  {n.approvedAt?.toDate?.().toLocaleString() || 'saved'}
                </div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>
                  phase {PHASES[n.phaseAtTimeOfReport]?.name || n.phaseAtTimeOfReport || '—'}
                  {n.approvedByName ? ` · approved by ${n.approvedByName}` : ''}
                  {n.source === 'ai-clinical-coordinator' ? ' · drafted automatically' : ''}
                  {n.confidence ? ` · confidence ${n.confidence}` : ''}
                </div>
              </div>
              <button onClick={() => setOpenNote(openNote === n.id ? null : n.id)}
                style={{ background: 'none', border: '1px solid rgba(100,181,246,0.4)',
                         color: '#64b5f6', borderRadius: '6px', padding: '4px 12px',
                         fontSize: '12px', cursor: 'pointer' }}>
                {openNote === n.id ? 'Close' : 'Open'}
              </button>
            </div>

            {openNote === n.id && (
              <div id={`saved-report-${n.id}`}
                   style={{ marginTop: '8px', padding: '20px 24px', ...PAPER,
                            borderLeft: '4px solid #2563eb' }}>
                <h4 style={{ margin: '0 0 2px 0', color: INK.strong, fontSize: '17px' }}>
                  Session Report — {patient.displayName}
                </h4>
                <p style={{ margin: '0 0 14px 0', color: INK.faint, fontSize: '12px' }}>
                  Approved {n.approvedAt?.toDate?.().toLocaleString() || ''}
                  {n.approvedByName ? ` by ${n.approvedByName}` : ''}
                  {' · '}phase {PHASES[n.phaseAtTimeOfReport]?.name || n.phaseAtTimeOfReport || '—'}
                  {' · '}confidence {n.confidence || '—'} · recommends only; decides nothing
                </p>

                <SessionReportBody r={n} />

                <div className="no-print" style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
                  <button onClick={() => printSaved(n)} style={{
                    padding: '8px 16px', backgroundColor: 'rgba(100,181,246,0.2)',
                    border: '1px solid rgba(100,181,246,0.5)', borderRadius: '8px',
                    color: '#1d4ed8', fontWeight: '500', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <FileText size={16} /> Print / PDF
                  </button>
                  <button onClick={() => deleteSavedNote(n)} style={{
                    padding: '8px 16px', backgroundColor: '#ffffff',
                    border: '1px solid #fca5a5', borderRadius: '8px',
                    color: '#991b1b', fontWeight: '500', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <X size={16} /> Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Assessment code — the Analyzer's reading, recomputed here. Works
          however the Analyzer was run: from this chart, from a bookmark, on
          another machine, or read off a printed report. */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Add an Assessment</h3>
          <span style={{ fontSize: '11px', color: '#64748b' }}>
            {INSTRUMENT_VERSION} · eight characters from the Analyzer report
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={code}
            onChange={e => { setCode(e.target.value); setCodeError(null); setCodePreview(null); }}
            onKeyDown={e => { if (e.key === 'Enter') readCode(); }}
            placeholder="NMA4-XXXXXXXX-C"
            spellCheck={false}
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '15px', letterSpacing: '0.12em', padding: '10px 12px',
              borderRadius: '8px', border: '1px solid rgba(100,116,139,0.5)',
              background: 'rgba(15,23,42,0.5)', color: '#e2e8f0', minWidth: '240px'
            }} />
          <button onClick={readCode} style={{
            padding: '10px 18px', borderRadius: '8px', border: 'none',
            backgroundColor: '#2563eb', color: '#ffffff',
            fontWeight: '600', cursor: 'pointer'
          }}>Read code</button>
        </div>

        <div style={{ color: '#64748b', fontSize: '12px', marginTop: '10px', lineHeight: 1.7 }}>
          <div><strong style={{ color: '#94a3b8' }}>Where the code comes from.</strong>{' '}
            The Analyzer prints it on the report, just under the state class. Run an
            assessment, and it is the line beginning <span style={{
              fontFamily: 'ui-monospace, Menlo, monospace' }}>NMA4-</span>.</div>
          <div style={{ marginTop: '4px' }}>
            <strong style={{ color: '#94a3b8' }}>You only need it for assessments taken elsewhere.</strong>{' '}
            Launch the Analyzer from this chart (Patient Tools, below) and the assessment
            arrives on its own. The code is for when it was run somewhere else — another
            machine, a bookmark, or read off a printout.
          </div>
          <div style={{ marginTop: '4px' }}>
            <strong style={{ color: '#94a3b8' }}>What it carries.</strong>{' '}
            All 39 answers, not a summary. Rebuild recomputes the reading rather than
            copying it, and refuses a code from a different version of the instrument.
          </div>
        </div>

        {codeError && (
          <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
                        fontSize: '13px', background: 'rgba(239,68,68,0.12)',
                        border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444' }}>
            {codeError}
          </div>
        )}

        {codePreview && (
          <div style={{ marginTop: '12px', padding: '14px 16px', ...PAPER }}>
            <div style={{ color: INK.strong, fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
              {codePreview.payload.bofOutput.class}
              {codePreview.payload.bofOutput.flag && (
                <span style={{ color: '#991b1b', fontWeight: 700 }}> ⚠ {codePreview.payload.bofOutput.flag}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {profileEntries(codePreview.payload).map(([label, status]) => (
                <span key={label} style={statusChipStyle(status)}>{label}: {status}</span>
              ))}
            </div>
            <div style={{ color: INK.body, fontSize: '12px', marginBottom: '10px' }}>
              Analyzer phase {codePreview.payload.phase}
              {codePreview.payload.sentinels.length
                ? ` · sentinel: ${codePreview.payload.sentinels.join(', ')}` : ''}
            </div>
            <button onClick={addCodeToChart} disabled={codeSaving} style={{
              padding: '8px 16px', backgroundColor: '#22c55e', border: 'none',
              borderRadius: '8px', color: 'white', fontWeight: '600',
              cursor: codeSaving ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <CheckCircle size={16} /> {codeSaving ? 'Adding…' : 'Add to chart'}
            </button>
          </div>
        )}
      </div>

      {/* Session Report — generated on demand, printed, and saved by the
          clinician. Before this, nothing in Rebuild could produce a report or
          keep one: generateTreatmentPlan existed and nothing called it. */}
      <div className="detail-section no-print">
        <div className="section-header">
          <h3>Session Report</h3>
          <span style={{ fontSize: '11px', color: '#64748b' }}>
            drafted for you · you approve and save
          </span>
        </div>

        {!draftReport && (
          <div style={{ padding: '4px 0 8px' }}>
            <button onClick={generateSessionReport} disabled={draftLoading}
              style={{
                padding: '10px 18px', backgroundColor: draftLoading ? 'rgba(100,116,139,0.3)' : '#64b5f6',
                border: 'none', borderRadius: '8px', color: draftLoading ? '#94a3b8' : '#0f172a',
                fontWeight: '600', cursor: draftLoading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
              <FileText size={16} />
              {draftLoading ? 'Drafting from the chart…' : 'Generate Session Report'}
            </button>
            <p style={{ color: '#64748b', fontSize: '12px', marginTop: '8px' }}>
              Draws on this chart's Analyzer assessments, imported documents, recorded
              conversation and activity. Nothing is saved until you approve it.
            </p>
          </div>
        )}

        {draftError && (
          <div style={{ padding: '10px 12px', borderRadius: '8px', fontSize: '13px',
                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)',
                        color: '#ef4444' }}>{draftError}</div>
        )}
      </div>

      {draftReport && (
        <div id="session-report-print" style={{
          padding: '20px 24px', marginBottom: '20px', ...PAPER,
          borderLeft: '4px solid #2563eb'
        }}>
          <h4 style={{ margin: '0 0 2px 0', color: INK.strong, fontSize: '17px' }}>
            Session Report — {patient.displayName}
          </h4>
          <p style={{ margin: '0 0 14px 0', color: INK.faint, fontSize: '12px' }}>
            {new Date().toLocaleString()} · phase {PHASES[patient.phase]?.name || patient.phase} ·{' '}
            {draftReport.documentsConsidered} document{draftReport.documentsConsidered === 1 ? '' : 's'},{' '}
            {draftReport.conversationTurns} recorded message{draftReport.conversationTurns === 1 ? '' : 's'},{' '}
            {draftReport.activitiesConsidered} activities · confidence {draftReport.confidence}
            {' '}· recommends only; decides nothing
          </p>

          <SessionReportBody r={draftReport} />

          <div className="no-print" style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button onClick={saveDraftReport} style={{
              padding: '8px 16px', backgroundColor: '#22c55e', border: 'none',
              borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <CheckCircle size={16} /> Approve &amp; Save to Record
            </button>
            <button onClick={printReport} style={{
              padding: '8px 16px', backgroundColor: 'rgba(100,181,246,0.2)',
              border: '1px solid rgba(100,181,246,0.5)', borderRadius: '8px',
              color: '#64b5f6', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <FileText size={16} /> Print / PDF
            </button>
            <button onClick={() => setDraftReport(null)} style={{
              padding: '8px 16px', backgroundColor: '#ffffff',
              border: '1px solid #cbd5e1', borderRadius: '8px',
              color: '#475569', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <X size={16} /> Discard
            </button>
          </div>
        </div>
      )}

      {/* AI Clinical Coordinator */}
      <div className="detail-section">
        <div className="section-header">
          <h3>AI Clinical Coordinator</h3>
          <span style={{ fontSize: '11px', color: '#a855f7' }}>NMA-16 Governance</span>
        </div>
        <AIAssistant 
          patient={patient}
          activities={activities}
        />
      </div>


      {/* Phase Change Modal */}
      {showPhaseChange && (
        <div className="modal-overlay" onClick={() => setShowPhaseChange(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Phase</h3>
              <button onClick={() => setShowPhaseChange(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Select the new phase for {patient.displayName}:</p>
              <div className="phase-options">
                {Object.entries(PHASES).map(([key, p]) => (
                  <button
                    key={key}
                    className={`phase-option ${patient.phase === key ? 'current' : ''}`}
                    style={{ borderColor: p.color }}
                    onClick={() => changePhase(key)}
                    disabled={patient.phase === key}
                  >
                    <span className="phase-dot" style={{ backgroundColor: p.color }} />
                    <div>
                      <strong>{p.name}</strong>
                      <span>{p.description}</span>
                    </div>
                    {patient.phase === key && <span className="current-badge">Current</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: '#ef4444' }}>Delete Patient</h3>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{patient.displayName}</strong>?</p>
              <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                This action cannot be undone. All patient data will be permanently removed.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button 
                  className="section-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  className="section-btn"
                  onClick={deletePatient}
                  style={{ backgroundColor: '#ef4444', color: 'white', borderColor: '#ef4444' }}
                >
                  Delete Patient
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tool Configuration Modal */}
      {showToolConfig && (
        <div className="modal-overlay" onClick={() => setShowToolConfig(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Configure Tools for {patient.displayName}</h3>
              <button onClick={() => setShowToolConfig(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#94a3b8' }}>
                Select which tools this patient can see and access:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '12px',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '4px'
              }}>
                {Object.entries(TOOLS).filter(([_, tool]) => !tool.clinicianOnly).map(([key, tool]) => {
                  const Icon = tool.icon;
                  const isVisible = visibleTools.includes(key);
                  return (
                    <div
                      key={key}
                      onClick={() => toggleToolVisibility(key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: isVisible ? 'rgba(34, 197, 94, 0.15)' : 'rgba(100, 116, 139, 0.1)',
                        border: isVisible ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(100, 116, 139, 0.3)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: isVisible ? '2px solid #22c55e' : '2px solid #64748b',
                        backgroundColor: isVisible ? '#22c55e' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {isVisible && <CheckCircle size={14} style={{ color: 'white' }} />}
                      </div>
                      <Icon size={20} style={{ color: isVisible ? '#22c55e' : '#64748b', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: '500', 
                          color: isVisible ? '#e2e8f0' : '#94a3b8',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {tool.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginTop: '24px', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  {visibleTools.length} tools selected
                </span>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button 
                    className="section-btn"
                    onClick={() => setShowToolConfig(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="section-btn"
                    onClick={saveToolVisibility}
                    style={{ backgroundColor: '#22c55e', color: 'white', borderColor: '#22c55e' }}
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ADD PATIENT MODAL
// ============================================

function AddPatientModal({ onClose, onSuccess, clinicianId, clinicianName }) {
  const [surname, setSurname] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create patient document (without Firebase Auth for now - simpler approach)
      // Kept short until launch: surname, first name, date of birth, sex.
      // `displayName` is derived rather than entered, so the dozen places that
      // already render it are untouched.
      const displayName = [firstName, surname].filter(Boolean).join(' ');
      const patientRef = await addDoc(collection(db, 'users'), {
        displayName,
        surname,
        firstName,
        email,
        dob,
        sex,
        role: 'patient',
        clinicianId,
        clinicianName,
        phase: 'unassessed',
        phaseStartedAt: serverTimestamp(),
        status: 'pending',
        consentGiven: false,
        createdAt: serverTimestamp()
      });

      setSuccess({
        patientId: patientRef.id,
        email,
        displayName
      });
      
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to create patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Add New Patient</h3>
          <button onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        {success ? (
          <div className="modal-body">
            <div className="success-message">
              <CheckCircle size={48} />
              <h4>Patient Added!</h4>
              <p>{success.displayName} has been added to your patient list.</p>
              <p className="success-note">They will receive an invitation to activate their account.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="auth-error">{error}</div>}
              
              <PDFUploadForForm
                onExtracted={(data) => {
                  // A referral gives a single name; split on the last space
                  // so the clinician can correct it rather than retype it.
                  const nm = (data.patient_info?.name || '').trim();
                  if (nm) {
                    const cut = nm.lastIndexOf(' ');
                    setSurname(cut > 0 ? nm.slice(cut + 1) : nm);
                    setFirstName(cut > 0 ? nm.slice(0, cut) : '');
                  }
                  if (data.patient_info?.dob) setDob(data.patient_info.dob);
                  if (data.patient_info?.sex) setSex(data.patient_info.sex);
                  if (data.patient_info?.email) setEmail(data.patient_info.email);
                }}
                onError={(err) => setError(err)}
              />
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Surname *</label>
                  <input
                    type="text"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    placeholder="Davies"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Betty"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="patient@email.com"
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Sex *</label>
                  <select
                    value={sex}
                    onChange={(e) => setSex(e.target.value)}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px',
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#e2e8f0',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-note" style={{ marginTop: '16px' }}>
                <AlertCircle size={16} />
                <span>Demographics should match EMR records. The phase is set by the first Analyzer assessment — until then the chart reads <strong>Not yet assessed</strong>.</span>
              </div>
            </div>
            
            <div className="modal-footer">
              <button type="button" className="modal-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="modal-btn primary" disabled={loading}>
                {loading ? 'Adding...' : 'Add Patient'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================
// CLINICAL TOOLS
// ============================================

function ClinicalTools() {
  const clinicalTools = Object.entries(TOOLS).filter(([key, tool]) => tool.clinicianOnly);
  const allTools = Object.entries(TOOLS).filter(([key, tool]) => !tool.clinicianOnly);

  const openTool = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div className="clinical-tools">
      <div className="tools-section">
        <h3>Assessment Tools</h3>
        <p>Clinical assessment tools for evaluating patient state</p>
        <div className="tools-grid">
          {clinicalTools.map(([key, tool]) => {
            const Icon = tool.icon;
            return (
              <button 
                key={key} 
                className="tool-card large"
                onClick={() => openTool(tool.url)}
              >
                <Icon size={32} />
                <span className="tool-name">{tool.name}</span>
                <span className="tool-desc">{tool.description}</span>
                <ExternalLink size={14} className="tool-external" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="tools-section">
        <h3>Patient Tools</h3>
        <p>Tools available to patients (preview access)</p>
        <div className="tools-grid">
          {allTools.map(([key, tool]) => {
            const Icon = tool.icon;
            return (
              <button 
                key={key} 
                className="tool-card"
                onClick={() => openTool(tool.url)}
              >
                <Icon size={24} />
                <span className="tool-name">{tool.name}</span>
                <span className="tool-desc">{tool.description}</span>
                <ExternalLink size={14} className="tool-external" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================
// CLINICIAN SETTINGS
// ============================================

function ClinicianSettings() {
  const { userProfile } = useAuth();
  const [pendingClinicians, setPendingClinicians] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  
  const isAdmin = userProfile?.email === 'droyelese@gmail.com';

  useEffect(() => {
    if (isAdmin) {
      loadPendingClinicians();
    } else {
      setLoadingPending(false);
    }
  }, [isAdmin]);

  const loadPendingClinicians = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'clinician'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      const pending = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingClinicians(pending);
    } catch (err) {
      console.error('Error loading pending clinicians:', err);
    } finally {
      setLoadingPending(false);
    }
  };

  const approveClinician = async (clinicianId) => {
    try {
      await updateDoc(doc(db, 'users', clinicianId), {
        status: 'active',
        approvedAt: serverTimestamp(),
        approvedBy: userProfile.uid
      });
      loadPendingClinicians();
    } catch (err) {
      console.error('Error approving clinician:', err);
    }
  };

  const rejectClinician = async (clinicianId) => {
    if (!window.confirm('Are you sure you want to reject this clinician?')) return;
    try {
      await deleteDoc(doc(db, 'users', clinicianId));
      loadPendingClinicians();
    } catch (err) {
      console.error('Error rejecting clinician:', err);
    }
  };

  return (
    <div className="settings-page">
      {isAdmin && (
        <div className="settings-section">
          <h3>Pending Clinician Approvals</h3>
          {loadingPending ? (
            <div className="loading-inline">Loading...</div>
          ) : pendingClinicians.length === 0 ? (
            <div className="settings-card">
              <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No pending approvals</p>
            </div>
          ) : (
            <div className="settings-card">
              {pendingClinicians.map(clinician => (
                <div key={clinician.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #eee' }}>
                  <div>
                    <strong>{clinician.name}</strong><br />
                    <span style={{ fontSize: '13px', color: '#666' }}>{clinician.email} - {clinician.credentials}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => approveClinician(clinician.id)} style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Approve</button>
                    <button onClick={() => rejectClinician(clinician.id)} style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="settings-section">
        <h3>Account Information</h3>
        <div className="settings-card">
          <div className="setting-row"><label>Name</label><span>{userProfile?.name}</span></div>
          <div className="setting-row"><label>Credentials</label><span>{userProfile?.credentials}</span></div>
          <div className="setting-row"><label>Email</label><span>{userProfile?.email}</span></div>
          <div className="setting-row"><label>Role</label><span>{userProfile?.role}{isAdmin ? ' (Admin)' : ''}</span></div>
        </div>
      </div>
      <div className="settings-section">
        <h3>About NM Rebuild</h3>
        <div className="settings-card">
          <p>NM Rebuild is the AI Clinical Coordinator for the Navigational Mind Framework.</p>
          <p className="version">Version 2.2 - NMA-16 Governance</p>
          <p><a href="https://navigationalmind.com" target="_blank" rel="noopener noreferrer">navigationalmind.com <ExternalLink size={12} /></a></p>
        </div>
      </div>
    </div>
  );
}
// ============================================
// PATIENT DASHBOARD
// ============================================

function PatientDashboard() {
  const { userProfile, logout } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadActivities();
  }, [userProfile]);

  const loadActivities = async () => {
    if (!userProfile?.uid) return;
    
    try {
      const q = query(
        collection(db, 'activities'),
        where('patientId', '==', userProfile.uid),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      const snapshot = await getDocs(q);
      const activityList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActivities(activityList);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const phase = PHASES[userProfile?.phase] || PHASES.unassessed;
  
  // Get visible tools - if visibleTools is set, use it; otherwise show all non-clinician tools
  const visibleToolKeys = userProfile?.visibleTools || Object.keys(TOOLS).filter(k => !TOOLS[k].clinicianOnly);
  const availableTools = Object.entries(TOOLS).filter(([key, tool]) => 
    !tool.clinicianOnly && visibleToolKeys.includes(key)
  );

  const launchTool = (tool, toolKey) => {
    const token = btoa(JSON.stringify({
      patientId: userProfile.uid,
      tool: toolKey,
      timestamp: Date.now(),
      // Patient demographics for tools like Analyzer
      patientInfo: {
        surname:   userProfile.surname   || '',
        firstName: userProfile.firstName || '',
        name:      userProfile.displayName || '',
        dob: userProfile.dob || '',
        sex: userProfile.sex || ''
      }
    }));
    const url = `${tool.url}?rebuild_token=${token}`;
    window.open(url, '_blank');
  };

  if (!userProfile?.consentGiven && userProfile?.status === 'pending') {
    return <ConsentPage onConsent={() => window.location.reload()} />;
  }

  return (
    <div className="patient-dashboard">
      <header className="patient-header">
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <Menu size={24} />
        </button>
        <div className="header-brand">
          <h1>NM Rebuild</h1>
        </div>
        <div className="header-user">
          <span>{userProfile?.displayName}</span>
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="patient-main">
        {/* Phase Banner */}
        <div className="phase-banner" style={{ backgroundColor: phase.color }}>
          <div className="phase-banner-content">
            <span className="phase-label">Current Phase</span>
            <h2>{phase.name}</h2>
            <p>{phase.description}</p>
          </div>
          <div className="phase-banner-meta">
            <span>Clinician: {userProfile?.clinicianName}</span>
          </div>
        </div>

        {/* Tools Section */}
        <section className="patient-section">
          <h3>Your Tools</h3>
          <p>Available navigation tools</p>
          <div className="patient-tools-grid">
            {availableTools.map(([key, tool]) => {
              const Icon = tool.icon;
              return (
                <button 
                  key={key} 
                  className="patient-tool-card"
                  onClick={() => launchTool(tool, key)}
                >
                  <div className="tool-icon">
                    <Icon size={28} />
                  </div>
                  <div className="tool-content">
                    <h4>{tool.name}</h4>
                    <p>{tool.description}</p>
                  </div>
                  <ExternalLink size={16} className="tool-arrow" />
                </button>
              );
            })}
          </div>
        </section>

        {/* Activity Section */}
        <section className="patient-section">
          <h3>Recent Activity</h3>
          {loading ? (
            <div className="loading-inline">Loading...</div>
          ) : activities.length === 0 ? (
            <div className="empty-state small">
              <Activity size={24} />
              <p>Start using a tool above to track your progress</p>
            </div>
          ) : (
            <div className="activity-list">
              {activities.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    <Activity size={16} />
                  </div>
                  <div className="activity-info">
                    <span className="activity-event">{activity.event}</span>
                    <span className="activity-tool">{TOOLS[activity.tool]?.name || activity.tool}</span>
                  </div>
                  <div className="activity-time">
                    {activity.timestamp?.toDate?.().toLocaleString() || 'Unknown'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="patient-footer">
        <p>Part of the Navigational Mind Framework</p>
        <a href="https://navigationalmind.com" target="_blank" rel="noopener noreferrer">
          navigationalmind.com
        </a>
      </footer>
    </div>
  );
}

// ============================================
// CONSENT PAGE
// ============================================

function ConsentPage({ onConsent }) {
  const { userProfile } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConsent = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.uid), {
        consentGiven: true,
        consentTimestamp: serverTimestamp(),
        status: 'active'
      });
      onConsent();
    } catch (err) {
      console.error('Error saving consent:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="consent-page">
      <div className="consent-container">
        <div className="consent-header">
          <h1>NM Rebuild</h1>
          <p>Welcome, {userProfile?.displayName}</p>
        </div>

        <div className="consent-body">
          <h2>Consent Agreement</h2>
          
          <div className="consent-text">
            <p>Before using NM Rebuild and connected tools, please review and agree to the following:</p>
            
            <h4>1. Purpose</h4>
            <p>NM Rebuild coordinates your treatment using tools from the Navigational Mind Framework. Your clinician ({userProfile?.clinicianName}) will have access to your activity and progress.</p>
            
            <h4>2. Data Collection</h4>
            <p>Your activity across connected tools (SWATD, Clarity, Console, etc.) will be recorded and shared with your clinician. This includes:</p>
            <ul>
              <li>Tool usage and session duration</li>
              <li>Progress metrics and completed exercises</li>
              <li>Phase transitions and recommendations</li>
            </ul>
            
            <h4>3. Data Storage</h4>
            <p>Minimal demographic information is stored online. Full treatment data is downloaded and stored locally by your clinician. No date of birth, health care numbers, or addresses are retained online.</p>
            
            <h4>4. Your Rights</h4>
            <p>You may withdraw from the program at any time by contacting your clinician. Program access requires an identified clinician.</p>
          </div>

          <label className="consent-checkbox">
            <input 
              type="checkbox" 
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>I have read and agree to the above terms</span>
          </label>

          <button 
            className="consent-btn"
            disabled={!agreed || loading}
            onClick={handleConsent}
          >
            {loading ? 'Submitting...' : 'I Agree — Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LANDING PAGE
// ============================================

function LandingPage() {
  // Check for patient param in URL OR localStorage (returning from a tool)
  const urlParams = new URLSearchParams(window.location.search);
  const returnToPatientURL = urlParams.get('patient');
  const returnToPatientStorage = localStorage.getItem('returnToPatient');
  const returnToPatient = returnToPatientURL || returnToPatientStorage;
  
  // If returning from a tool with patient context, redirect to login
  useEffect(() => {
    if (returnToPatient) {
      // Store in localStorage if from URL (for persistence through login)
      if (returnToPatientURL) {
        localStorage.setItem('returnToPatient', returnToPatientURL);
      }
      // Redirect to login - LoginPage will handle the rest
      window.location.href = '/login';
    }
  }, [returnToPatient, returnToPatientURL]);

  return (
    <div className="landing-page">
      <div style={{ background: "#080c14", padding: "8px 0", borderBottom: "1px solid rgba(100,116,139,0.15)" }}><div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}><a href="https://navigationalmind.com" style={{ fontFamily: "monospace", fontSize: 13, color: "#e2e8f0", textDecoration: "none", letterSpacing: "0.5px", display: "inline-flex", alignItems: "center", gap: 6 }}>← navigationalmind.com</a></div></div>
      <header className="landing-header">
        <h1>NM Rebuild</h1>
        <p>AI Clinical Coordinator</p>
      </header>

      <main className="landing-main">
        <div className="landing-hero">
          <h2>Coordinated Navigation Support</h2>
          <p>NM Rebuild connects patients with the right tools at the right time, guided by clinician oversight and NMA-16 governance.</p>
        </div>

        <div className="landing-cards">
          <a href="/login" className="landing-card">
            <User size={32} />
            <h3>Patient Login</h3>
            <p>Access your tools and track progress</p>
          </a>
          
          <a href="/login" className="landing-card">
            <Users size={32} />
            <h3>Clinician Login</h3>
            <p>Manage patients and monitor treatment</p>
          </a>
          
          <a href="/register" className="landing-card highlight">
            <Plus size={32} />
            <h3>Clinician Registration</h3>
            <p>New clinician? Register here</p>
          </a>
        </div>
      {/* What this is — a <details> dropdown. No JavaScript, closed by default:
            it is for the reader who does not yet know what any of this is, and
            must not detain the one who does. */}
        <details className="explainer">
          <summary>What is NM Rebuild?</summary>
          <div className="explainer-body">
            <p>
              The place a patient's care is held over time — the sequence of
              assessments, the documents, and the notes a clinician approves.
            </p>
            <p>
              The state itself comes from the <strong>NM Analyzer</strong>, a separate
              deterministic instrument. Rebuild's AI Coordinator organizes, collates
              visits for continuity, and recommends — but it <strong>never decides,
              never diagnoses, and cannot change the reading the Analyzer
              determined</strong>.
            </p>
            <p>
              Every report is printed in two labelled halves, so a reader can always
              see which part is the instrument and which is inference.
            </p>
            <a className="explainer-link" href="/NM-Rebuild.pdf" target="_blank" rel="noopener noreferrer">
              Read the full explanation (PDF, 2 pages)
            </a>
            <a className="explainer-link secondary" href="/NM-Rebuild.pdf" download="NM-Rebuild.pdf">
              Download it
            </a>
          </div>
        </details>

      </main>

      <footer className="landing-footer">
        <p>Part of the Navigational Mind Framework</p>
        <a href="https://navigationalmind.com" target="_blank" rel="noopener noreferrer">
          navigationalmind.com
        </a>
      </footer>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route 
          path="/clinician/*" 
          element={
            <ProtectedRoute requireClinician>
              <ClinicianDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/patient/*" 
          element={
            <ProtectedRoute requirePatient>
              <PatientDashboard />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
